import { useEffect, useRef, useState } from "react";
import zoomSdk from "@zoom/appssdk";
import { AIMS_STATUS, SUMMARIZE_STATES, ROLES, START_DISCLAIMER_CHAT_MESSAGE } from "../constants";
import { fetchRtmsSessionStatus, SESSION_STATUS } from "../utils/rtmsSessionStatusUtils";
import { usePreference } from "../context/PreferenceContext";
import LogManager from "../utils/logManager";

const SUMMARIZATION_ELIGIBLE_STATUSES = [
  AIMS_STATUS.AVAILABLE,
  AIMS_STATUS.SUMMARY_TO_ORGANIZER,
];

/**
 * Auto-starts summarization for organizers who opted in. Used by both legacy and default UI.
 * Transcript auto-start is separate (useInMeetingInitialization).
 *
 * Returns isAutoStarting (show loader), autoStartFailed (show warning modal),
 * dismissAutoStartFailed, autoStartCompleted.
 */
const useAutoStartSummarization = ({ role, aimsStatus, summarizeState, meetingUuid, appContext, onSummarize }) => {
  const { isLoaded, loadFailed, loadFailureStatus, initialAutoStart } = usePreference();
  const guardRef = useRef(false);
  const onSummarizeRef = useRef(onSummarize);
  const [isAutoStarting, setIsAutoStarting] = useState(false);
  const [autoStartFailed, setAutoStartFailed] = useState(false);
  const [autoStartCompleted, setAutoStartCompleted] = useState(false);

  useEffect(() => {
    onSummarizeRef.current = onSummarize;
  }, [onSummarize]);

  const dismissAutoStartFailed = () => setAutoStartFailed(false);

  useEffect(() => {
    if (!isLoaded) return;
    if (role !== ROLES.ORGANIZER) {
      LogManager.info("Auto-start summarization: skipped, role =", role);
      return;
    }
    if (!SUMMARIZATION_ELIGIBLE_STATUSES.includes(aimsStatus)) {
      LogManager.info("Auto-start summarization: skipped, aimsStatus =", aimsStatus);
      return;
    }
    if (summarizeState !== SUMMARIZE_STATES.DEFAULT) {
      LogManager.info("Auto-start summarization: skipped, summarizeState =", summarizeState);
      return;
    }
    if (!appContext) {
      LogManager.info("Auto-start summarization: waiting for appContext");
      return;
    }
    if (guardRef.current) {
      LogManager.info("Auto-start summarization: skipped, already attempted");
      return;
    }

    // Preferences unreadable. 403 is by design (HIRE allowlist): the opt-in is unknown, so skip
    // quietly. Any other failure is a real outage and shows the failure modal.
    if (loadFailed) {
      // Once per mount, so returning to DEFAULT doesn't re-raise the modal.
      guardRef.current = true;
      if (loadFailureStatus === 403) {
        LogManager.info(
          "Auto-start summarization: not applicable, /preferences denied (403) so the opt-in cannot be read"
        );
        // Separate metric: AutoStart_Skipped can't filter by `reason` (property, not dimension).
        LogManager.metric("AutoStartSummarization_PreferencesDenied", 1);
        return;
      }

      LogManager.error(
        "Auto-start summarization: FAILED - /preferences unreadable, status =",
        loadFailureStatus ?? "none"
      );
      LogManager.metric("AutoStart_Failed", 1, { reason: "preference_api_error" });
      setAutoStartFailed(true);
      return;
    }

    if (initialAutoStart !== true) {
      LogManager.info("Auto-start summarization: skipped, preference not enabled (initialAutoStart =", initialAutoStart, ")");
      LogManager.metric("AutoStart_Skipped", 1, { reason: "preference_disabled" });
      return;
    }

    LogManager.info("Auto-start summarization: all conditions met, checking session status for meeting:", meetingUuid);

    const checkAndAutoStart = async () => {
      setIsAutoStarting(true);
      try {
        const sessionStatus = await fetchRtmsSessionStatus(meetingUuid);
        LogManager.info("Auto-start summarization: /session returned status =", sessionStatus.status);

        if (sessionStatus.status === SESSION_STATUS.CONNECT_FAIL || sessionStatus.status === SESSION_STATUS.ERROR) {
          LogManager.error("Auto-start summarization: FAILED - /session returned", sessionStatus.status);
          LogManager.metric("AutoStart_Failed", 1, { reason: "session_" + sessionStatus.status });
          setIsAutoStarting(false);
          setAutoStartFailed(true);
          setAutoStartCompleted(true);
          return;
        }

        if (sessionStatus.status !== SESSION_STATUS.NOT_FOUND) {
          LogManager.info("Auto-start summarization: skipped, session already exists with status:", sessionStatus.status);
          LogManager.metric("AutoStart_Skipped", 1, { reason: "session_exists" });
          guardRef.current = true;
          setIsAutoStarting(false);
          setAutoStartCompleted(true);
          return;
        }

        guardRef.current = true;
        LogManager.info("Auto-start summarization: SUCCESS - triggering onSummarize for meeting:", meetingUuid);
        LogManager.metric("AutoStart_Triggered", 1);
        try {
          await zoomSdk.sendMessageToChat({
            message: START_DISCLAIMER_CHAT_MESSAGE,
          });
          LogManager.info("Auto-start summarization: sent disclaimer message to meeting chat");
        } catch (error) {
          LogManager.error("Auto-start summarization: failed to send disclaimer message:", error);
        }
        // Invoke the controller directly so auto-start does not open the manual-start disclaimer modal.
        onSummarizeRef.current();
        setIsAutoStarting(false);
        setAutoStartCompleted(true);
      } catch (error) {
        LogManager.error("Auto-start summarization: FAILED - session check error, falling back to manual:", error);
        LogManager.metric("AutoStart_Failed", 1, { reason: "session_check_error" });
        setIsAutoStarting(false);
        setAutoStartFailed(true);
        setAutoStartCompleted(true);
      }
    };

    checkAndAutoStart();
  }, [meetingUuid, role, aimsStatus, summarizeState, isLoaded, appContext, loadFailed, loadFailureStatus, initialAutoStart]);

  return { isAutoStarting, autoStartFailed, dismissAutoStartFailed, autoStartCompleted };
};

export default useAutoStartSummarization;
